// The mixer's gain staging (P4.2) — FR-14, and Architecture §4.2's fixed topology.
//
// Here rather than in `/audio` for the reason `sync.ts` and `loop.ts` are here: **the UI needs half
// of this and the worklet needs the other half**, and `ui-no-engine-internals` exists so neither
// drags the other into its bundle. A trim knob has to know where unity sits in order to draw itself;
// the audio thread has to know the same number in order to apply it. One definition, two readers.
//
// ⚠️ The import below is this file's first, and it is a sibling in `platform/types` rather than a
// reach into `/audio` — `STRIP_SIGNAL_SOURCE` maps a strip to the FR-26 source it publishes, which
// is exactly the "UI needs half, worklet needs the other half" shape the header describes.

import { SIGNAL_SOURCE, type SignalSourceIndex } from './rings';

//
// ============================================================================
// HEADROOM — a decision, written down before the limiter exists to hide it
// ============================================================================
//
// P4.2's rule: *"Headroom is a decision, not an accident: state where unity is, what two decks at
// unity sum to, and what the master expects, because P4.6's limiter will otherwise be asked to fix a
// choice nobody made."* So, in order.
//
// **1. Where unity is.** Trim at centre is exactly 1.0 — the strip passes the deck's output through
// unchanged. Not 0.5, not "−6 dB to leave room for the second deck". A channel that attenuated by
// default would make *one* deck playing alone quieter than the file it came from, which is a lie
// about the material and the first thing a user would compensate for by pushing everything else up.
//
// **2. What arrives at unity.** FR-10 normalises every track toward `LOUDNESS_TARGET_LUFS` (−14),
// with peaks capped at `LOUDNESS_PEAK_CEILING` (−1 dBFS). So a channel at unity is carrying roughly
// −14 LUFS with peaks near −1 dBFS, and that is the number the sum below is computed from.
//
// **3. What two decks at unity sum to — and the answer is the pessimistic one, because of what this
// program is for.**
//
// Two uncorrelated signals add in *power*: +3.01 dB. Two correlated signals add in *amplitude*:
// +6.02 dB. Most audio software quotes the first, and for most audio software that is right.
//
// ⚠️ **A DJ mixer must quote the second, because beatmatching is the act of correlating them.** FR-07
// exists to put deck A's kick on top of deck B's kick, to within 10 ms. At the moment a blend is
// working — the moment the whole product is for — the two loudest transients in the mix are aligned
// and adding in amplitude. Designing for +3 dB and meeting +6 dB is how a mixer ships that clips
// exactly when it is being used properly.
//
// So: **two decks at unity, correlated, sum to +6.02 dB**, with peaks reaching about **+5 dBFS**.
//
// **4. What the master expects.** Peaks above full scale, routinely, by design.
//
// This is safe *inside* the graph and only inside it: every buffer from here to the device is
// float32, and float32 does not clip — a sample of 1.8 is simply a sample of 1.8, carried without
// distortion through every stage. Clipping happens in exactly one place, the conversion to the
// output device, and nowhere before it.
//
// ⚠️ **That is the real argument for FR-16's limiter being always-on, and it is stronger than
// "protection".** The limiter is not a safety net bolted onto a design that would otherwise be fine;
// it is this design's *terminating condition*. Gain staging that lets two aligned kicks sum freely is
// only coherent if something deterministic stands at the output, and P4.6 is that something. A
// bypass switch on it would not be a feature — it would remove the only stage that makes the
// arithmetic above legal.
//
// **What this pack must not do**, recorded here because it is the tempting shortcut: quietly
// attenuate each channel by 6 dB so the sum lands at unity. It would make the numbers tidy, make the
// limiter look unnecessary, and make a single deck 6 dB quieter than every other player on the
// machine. The limiter is the honest answer and the trim is where a human overrides it.

/**
 * How far trim moves the channel, in decibels either side of unity.
 *
 * ±12 dB, matching `LOUDNESS_MAX_GAIN_DB` deliberately: FR-10 will boost a quiet master by at most
 * 12 dB automatically, and trim is the manual complement to that automation. Equal authority in both
 * directions means a user can always reach, by hand, any correction the analyser could have made —
 * and undo one it made wrongly.
 *
 * Wider would be a channel that can be driven far enough to make the limiter work continuously,
 * which is a sound, not a setting. Narrower would leave a genuinely quiet record unreachable.
 */
export const TRIM_RANGE_DB = 12;

/** Trim at unity: the strip passes the deck through unchanged. */
export const TRIM_UNITY_GAIN = 1;

/**
 * The fader position that means unity, on the 0…1 the UI works in.
 *
 * Centre, because trim is bipolar — it cuts as readily as it boosts, and a control whose neutral
 * point is not its middle is one a hand cannot find without looking.
 */
export const TRIM_UNITY_POSITION = 0.5;

/** Decibels to a linear gain. */
export function gainFromDb(db: number): number {
  return Math.pow(10, db / 20);
}

/** Linear gain to decibels. Silence is −Infinity rather than NaN, which a readout can render. */
export function dbFromGain(gain: number): number {
  return gain <= 0 ? Number.NEGATIVE_INFINITY : 20 * Math.log10(gain);
}

/**
 * A trim knob's 0…1 position to the linear gain it means.
 *
 * Linear **in decibels**, not in amplitude, because a hand moving a gain control expects equal
 * movements to make equal-sounding changes and loudness is logarithmic. A position-linear amplitude
 * map would put unity at 0.5 of the *gain* range and crowd every useful setting into the top of the
 * travel.
 *
 * Out-of-range positions are clamped rather than refused: unlike a tapped tempo, a position outside
 * 0…1 has an unambiguous intended meaning and no user typed it — it is a rounding artefact from a
 * pointer or a MIDI controller, and refusing it would mean a knob that sticks near its ends.
 */
export function trimGainFromPosition(position: number): number {
  if (!Number.isFinite(position)) {
    return TRIM_UNITY_GAIN;
  }
  const clamped = Math.min(1, Math.max(0, position));
  const db = ((clamped - TRIM_UNITY_POSITION) / TRIM_UNITY_POSITION) * TRIM_RANGE_DB;
  return gainFromDb(db);
}

/** The inverse, for a UI restoring a stored gain onto a knob. */
export function trimPositionFromGain(gain: number): number {
  if (!(gain > 0) || !Number.isFinite(gain)) {
    return TRIM_UNITY_POSITION;
  }
  const db = Math.min(TRIM_RANGE_DB, Math.max(-TRIM_RANGE_DB, dbFromGain(gain)));
  return TRIM_UNITY_POSITION + (db / TRIM_RANGE_DB) * TRIM_UNITY_POSITION;
}

/**
 * How much louder `count` channels at unity are than one of them, in decibels.
 *
 * **The arithmetic §3 of this file's header commits to, written as code so a test can measure a real
 * sum against it rather than against a comment.** That is the whole of P4.2's VERIFY: *"Two decks at
 * unity produce a measured sum that matches the stated headroom arithmetic."*
 *
 * - `correlated` — amplitudes add: `20·log10(n)`. Two aligned kicks, which is what Sync produces.
 * - uncorrelated — powers add: `10·log10(n)`. Two unrelated tracks, the everyday case.
 *
 * Both are exact for the idealised cases and neither is a fit to anything; real material lands
 * between them, and the design is sized for the top of the range.
 */
export function summedGainDb(count: number, correlated: boolean): number {
  if (count <= 0) {
    return Number.NEGATIVE_INFINITY;
  }
  return (correlated ? 20 : 10) * Math.log10(count);
}

// ---------------------------------------------------------------------------
// FR-16 — the master gain (P4.6). The limiter's own constants live in `audio/dsp/limiter.ts`,
// because nothing outside the audio thread needs them; these two are here because the UI draws
// the control and the worklet applies it.
// ---------------------------------------------------------------------------

/** Master gain at unity — the sum passes to the limiter unchanged. */
export const MASTER_GAIN_UNITY = 1;

/**
 * How far the master gain travels, in decibels either side of unity.
 *
 * ⚠️ **Asymmetric, and the asymmetry is the point.** −∞ to +6: a master fader must be able to close
 * completely, because "stop the sound" is the one thing it is reached for in a hurry, and a control
 * that bottoms out at −12 dB cannot do it. Upward it stops at +6 because the stage after it is a
 * brickwall — more boost buys no more loudness, only more gain reduction, and a control whose top
 * half does nothing but squash is a control that teaches the wrong thing about the mixer.
 */
export const MASTER_GAIN_MAX_DB = 6;

/** The linear bounds the slew's span is computed from. */
export const MASTER_GAIN_MAX = 10 ** (MASTER_GAIN_MAX_DB / 20);
export const MASTER_GAIN_MIN = 0;

/** How long the master gain takes to cross its whole travel. Trim's figure, for trim's reason. */
export const MASTER_GAIN_RAMP_SECONDS = 0.03;

/**
 * A master fader's 0…1 position to the linear gain it means.
 *
 * Unity sits at `MASTER_GAIN_UNITY_POSITION` rather than at the top, so there is travel above it —
 * and below unity the law is linear in **amplitude** for `faderGainFromPosition`'s reason: silence
 * has to be reachable, and a dB-linear law never arrives. Above unity it is linear in decibels,
 * because that half is a gain trim and behaves like one.
 */
export const MASTER_GAIN_UNITY_POSITION = 0.8;

export function masterGainFromPosition(position: number): number {
  if (!Number.isFinite(position)) {
    return MASTER_GAIN_UNITY;
  }
  const clamped = Math.min(1, Math.max(0, position));
  if (clamped <= MASTER_GAIN_UNITY_POSITION) {
    return (clamped / MASTER_GAIN_UNITY_POSITION) * MASTER_GAIN_UNITY;
  }
  const above = (clamped - MASTER_GAIN_UNITY_POSITION) / (1 - MASTER_GAIN_UNITY_POSITION);
  return gainFromDb(above * MASTER_GAIN_MAX_DB);
}

// ---------------------------------------------------------------------------
// FR-15 — the channel fader and the crossfader law (P4.5)
// ---------------------------------------------------------------------------

/**
 * A channel fader's 0…1 position to the linear gain it means.
 *
 * **Linear in amplitude, unlike trim — and the difference is the division of labour between them.**
 * `trimGainFromPosition` is linear in *decibels* because a hand correcting a quiet record wants
 * equal movements to make equal-sounding changes. A fader's job is different: it has to reach
 * **true silence** at the bottom, and a dB-linear law never does — it approaches −∞ and arrives
 * nowhere. A channel that cannot be closed is not a fader.
 *
 * So: 0 is silence, 1 is unity, and the two controls divide the work. Trim sets how loud the
 * channel *is*; the fader decides how much of it reaches the mix.
 *
 * ⚠️ **The taper is a by-ear decision this prompt does not make.** Real mixers bend this curve so
 * more of the useful range sits in the top of the travel; linear-in-amplitude feels bottom-heavy,
 * because perceived loudness is logarithmic and half the amplitude is only −6 dB. Whether that
 * matters here is a question for a hand on a real fader, not for a formula — parked in
 * BACKLOG-NOTES rather than guessed at.
 */
export function faderGainFromPosition(position: number): number {
  if (!Number.isFinite(position)) {
    return 0;
  }
  return Math.min(1, Math.max(0, position));
}

/**
 * FR-15's two crossfader curves. They are different instruments sharing a control.
 *
 * `smooth` is for a blend — the two tracks overlap for most of the travel and the sum holds its
 * level. `sharp` is for a cut — the channel is either in or out, and the middle of the travel has
 * both at full so a hand can chop between them.
 */
export const CROSSFADER_CURVE = {
  SMOOTH: 'smooth',
  SHARP: 'sharp',
} as const;

export type CrossfaderCurve = (typeof CROSSFADER_CURVE)[keyof typeof CROSSFADER_CURVE];

/** Both channels at equal gain. The crossfader's rest position. */
export const CROSSFADER_CENTRE = 0.5;

/**
 * How far from either end the sharp curve has **fully cut** the far channel.
 *
 * ⚠️ **A specified position, not "near the end"**, and the distinction is what makes the curve an
 * instrument rather than a taper. A cut DJ needs a *dead zone*: somewhere the channel is reliably,
 * completely silent without having to hit the exact edge of the travel. At 0.1 that zone is a tenth
 * of the fader, which a thumb can find without looking.
 *
 * The ramp back to full gain is the same width again, so the law needs one number rather than two:
 * silent at or below `CROSSFADER_SHARP_CUT`, full at or above twice it, linear between. That makes
 * the transition a tenth of the travel — sharp enough to chop with, and continuous, so the law does
 * not depend on the strip's slew limiter to avoid a discontinuity. The slew limiter still smooths
 * what remains, exactly as it does for trim.
 */
export const CROSSFADER_SHARP_CUT = 0.1;

/**
 * How far the summed power of a `smooth` crossfade may stray from unity, in decibels.
 *
 * ⚠️ **This is the number P4.5's VERIFY is about, and it is a band rather than a point because the
 * measurement is of real signals rather than of the formula.** `cos²+sin² = 1` is an identity and
 * testing it would only prove that JavaScript can do trigonometry; the test that matters sums two
 * uncorrelated signals across the sweep and measures what comes out.
 *
 * 0.5 dB is tight enough to fail the law this replaces — a linear `(1−x, x)` fade dips **3.01 dB**
 * at centre, six times the band — and loose enough that a finite measurement of finite signals is
 * not fighting its own sample count.
 */
export const CROSSFADER_POWER_TOLERANCE_DB = 0.5;

/**
 * The two channel gains a crossfader position means, as `[a, b]`.
 *
 * 0 is deck A alone, 1 is deck B alone, and `CROSSFADER_CENTRE` is both.
 *
 * ## Constant power, not constant amplitude — the whole point of the smooth curve
 *
 * The obvious fade is linear: `a = 1 − x`, `b = x`. It is wrong, audibly, and the reason is that
 * **two uncorrelated signals add in power rather than in amplitude.** At the midpoint a linear fade
 * has both channels at 0.5, so the summed power is `0.5² + 0.5² = 0.5` — **3 dB down**, a hole in
 * the middle of every blend. Every DJ has heard it; it is the dip that makes a transition sound
 * like a mistake.
 *
 * `cos`/`sin` in quadrature holds `a² + b² = 1` at every position, which is what "constant power"
 * means and why this is the standard law rather than a preference.
 *
 * ⚠️ **Correlated material still rises, by design, and that is the headroom decision above doing
 * its job.** Sync exists to align two kicks; aligned kicks add in *amplitude*, so a smooth crossfade
 * of beatmatched tracks peaks at `cos + sin = √2` — **+3 dB** at centre — and a `sharp` crossfade at
 * centre has both channels at unity, which is the **+6.02 dB** this file's header sizes the master
 * for. Neither is a fault to be corrected here. P4.6's limiter is the terminating condition, and
 * flattening the crossfader to hide it would be the "quietly attenuate each channel" shortcut this
 * file already refused.
 *
 * Out-of-range positions are clamped, for `trimGainFromPosition`'s reason: a pointer or a MIDI
 * controller can produce 1.0000001, and a fader that sticks near its ends is worse than one that
 * rounds.
 */
export function crossfaderGains(
  position: number,
  curve: CrossfaderCurve,
): readonly [number, number] {
  // ✅ **Both curves confirmed by ear on 13 September 2026**, on two playing decks — P4.5's VERIFY,
  // and the half no measurement supplies. A slow sweep of the smooth curve had **no dip at the
  // midpoint**, which is the whole reason it is constant-power and the one thing the arithmetic
  // above cannot prove about a listener. The sharp curve behaved as a switch, with a silent dead
  // zone at each end. A closed channel fader was silent rather than faint.
  //
  // Recorded here rather than only in a handoff because the numbers below are a claim about what
  // something sounds like, and the evidence for that claim should sit beside it (R-B).

  const x = Number.isFinite(position)
    ? Math.min(1, Math.max(0, position))
    : CROSSFADER_CENTRE;

  if (curve === CROSSFADER_CURVE.SHARP) {
    return [sharpGain(1 - x), sharpGain(x)];
  }

  // ⚠️ **The ends are exact by definition, because trigonometry is not.** `Math.cos(Math.PI / 2)` is
  // 6.12e-17 rather than 0 — inaudible as audio, and wrong as a *statement*: a crossfader pushed
  // fully across has closed the other channel, and a readout computed from that gain printed
  // "−324.3 dB" instead of "off". Found in the browser, not by the unit tests, because the tests
  // asked whether the gain was close to zero and it is.
  //
  // Snapped rather than thresholded: there is one position where the answer must be exact and it is
  // known in advance, so a comparison against an epsilon would be inventing a tolerance for a case
  // that does not need one. Just inside the end the channel really is at −76 dB, and saying so is
  // correct.
  if (x <= 0) {
    return [1, 0];
  }
  if (x >= 1) {
    return [0, 1];
  }

  // Quadrature: a² + b² = 1 at every x, which is the definition rather than an approximation of it.
  const angle = x * (Math.PI / 2);
  return [Math.cos(angle), Math.sin(angle)];
}

/**
 * One channel's sharp-curve gain, given how far the fader is *toward* it (0 = fully away).
 *
 * Silent through the dead zone, full from twice its width, linear between — see
 * `CROSSFADER_SHARP_CUT`.
 */
function sharpGain(toward: number): number {
  if (toward <= CROSSFADER_SHARP_CUT) {
    return 0;
  }
  if (toward >= CROSSFADER_SHARP_CUT * 2) {
    return 1;
  }
  return (toward - CROSSFADER_SHARP_CUT) / CROSSFADER_SHARP_CUT;
}

/**
 * How many channel strips the master sums.
 *
 * **Two, and §2 of the M4 pack rules on it rather than letting it drift.** The roadmap says three
 * (A / SMP / B), but the sampler that would feed `SMP` is FR-21 and lands at M6 — so a third strip
 * built now would run an EQ, a filter and two FX slots over silence for two milestones, on the one
 * thread where NFR-P1 and NFR-P2 live.
 *
 * It would also quietly settle PRD OQ-A2, "sampler routing: pads on their own channel strip vs.
 * assignable per-deck routing", in the direction of the current recommendation — which is exactly
 * the drift `ADR-LOG.md` exists to catch.
 *
 * ⚠️ **The master sums an array rather than two named inputs, and that is the whole mitigation.**
 * M6 adds a call site; it does not re-derive gain staging. See ADR-L13.
 */
export const MIXER_STRIP_COUNT = 2;

// ---------------------------------------------------------------------------
// The mixer processor's vocabulary
// ---------------------------------------------------------------------------

/** Registered name — see `audio-worklet.entry.ts`. */
export const MIXER_PROCESSOR = 'synapse-mixer';

/**
 * The mixer's opcodes, in **its own table** rather than sharing the deck's.
 *
 * `deck.ts` states the rule and the reason: "never renumber a value in place… Giving each processor
 * its own table is what makes that rule easy to keep — vocabularies that cannot collide by
 * construction do not need anyone to remember." Two sides of a ring ship in bundles that cache
 * separately, so a stale worklet reading a reused opcode would apply a trim as a fader.
 *
 * `COMMAND_RECORD.TARGET` already means "deck, channel, slot", so a strip index goes there and no
 * new record shape is needed.
 */
export const MIXER_OPCODE = {
  /** value = linear trim gain for the strip named by TARGET. See `trimGainFromPosition`. */
  SET_TRIM: 1,
  /**
   * value = linear band gain; TARGET packs the strip and the band (P4.3).
   *
   * One opcode rather than three, because the band is data and not an instruction — three opcodes
   * would be three places to keep in step with `EQ_BAND` and the first vocabulary drift this file's
   * own rule warns about.
   */
  SET_EQ_BAND: 2,
  /** value = the filter knob's 0…1 position for the strip named by TARGET (P4.4).
   *
   * ⚠️ The **position**, not a cutoff. The law that turns one into the other lives in
   * `filterFromPosition` and is needed on both sides — the UI draws a readout from it and the
   * worklet filters from it — so sending the raw knob keeps one definition rather than two. */
  SET_FILTER: 3,
  /** value = the channel fader's 0…1 position for the strip named by TARGET (P4.5). */
  SET_FADER: 4,
  /**
   * value = the crossfader's 0…1 position (P4.5). TARGET is unused — there is one crossfader.
   *
   * ⚠️ **The position, not two gains.** A crossfader move changes both strips, and two commands can
   * land in different blocks; for one block the mix would be whatever half a crossfade sounds like.
   * The audio thread applies `crossfaderGains` itself so the pair is always coherent.
   */
  SET_CROSSFADER: 5,
  /** value = a `CROSSFADER_CURVE_CODE` (P4.5). See there for why a curve crosses as a number. */
  SET_CROSSFADER_CURVE: 6,
  /** value = the master gain as a linear gain (P4.6). TARGET unused — there is one master. */
  SET_MASTER_GAIN: 7,
  /**
   * value = an `FX_UNIT` code; TARGET packs the strip and the slot (P4.8). See `packFxTarget`.
   *
   * One opcode for "which unit", as `SET_EQ_BAND` is one opcode for "which band": the slot is data
   * and not an instruction, and an opcode per unit would be a second table to keep in step with
   * `FX_UNIT` — the vocabulary drift this file's own rule warns about.
   */
  SET_FX_UNIT: 8,
  /** value = the slot's dry/wet position, 0…1 (P4.8). The **position**, not a pair of gains: the law
   * is `fxMixGains` and both sides need it — the UI to print a percentage, the worklet to apply it. */
  SET_FX_MIX: 9,
  /** value = an `FX_DIVISION` as a fraction of a beat (P4.8). Sent as the fraction rather than an
   * index, so the wire carries the musical meaning and `fxDivisionFromValue` snaps a stray f32 to
   * the nearest legal one. */
  SET_FX_DIVISION: 10,
  /** value = the echo's feedback position, 0…1 (P4.8). `echoFeedbackFromPosition` owns the ceiling —
   * see `ECHO_FEEDBACK_MAX`, which is a stability bound rather than taste. */
  SET_FX_FEEDBACK: 11,
  /** value = the reverb's decay knob position, 0…1 (P4.9). `reverbDecayFromPosition` owns the law —
   * the UI prints seconds from it and the worklet derives per-line gains from the same number. */
  SET_FX_DECAY: 12,
  /** value = the reverb's damping position, 0…1 (P4.9). 1 is darkest. See `PlateReverb.setDamping`:
   * this is the control that separates a room from a ringing tube. */
  SET_FX_DAMPING: 13,
} as const;

/**
 * The crossfader curves as numbers, because `COMMAND_RECORD.VALUE` is an f32 and a string is not.
 *
 * The same shape as `TRANSPORT_PHASE_CODE`: a name on both sides, a number on the wire, and one
 * table so the two cannot drift. ⚠️ Never renumber a value in place — two sides of the ring ship in
 * bundles that cache separately, and a stale worklet reading a reused code would select the other
 * curve silently.
 */
export const CROSSFADER_CURVE_CODE = {
  [CROSSFADER_CURVE.SMOOTH]: 0,
  [CROSSFADER_CURVE.SHARP]: 1,
} as const satisfies Record<CrossfaderCurve, number>;

/** The inverse, for the audio thread. Anything unrecognised is `smooth` — the safer of the two,
 * since it is the one that cannot put both channels at unity. */
export function crossfaderCurveFromCode(code: number): CrossfaderCurve {
  return code === CROSSFADER_CURVE_CODE[CROSSFADER_CURVE.SHARP]
    ? CROSSFADER_CURVE.SHARP
    : CROSSFADER_CURVE.SMOOTH;
}

/**
 * Packs a strip index and a band index into one `COMMAND_RECORD.TARGET` u16.
 *
 * The record has one target field and an EQ command addresses two things. Low byte is the strip,
 * high byte the band — both are small and bounded, and a u16 holds them with room to spare. The pack
 * and unpack live beside each other so they cannot drift apart, which is the failure P3.7's rule is
 * about: anything crossing `postMessage` may be keyed by value, and a value packed one way and read
 * another is the same bug wearing different clothes.
 */
export function packEqTarget(strip: number, band: number): number {
  return ((band & 0xff) << 8) | (strip & 0xff);
}

export function unpackEqTarget(target: number): { strip: number; band: number } {
  return { strip: target & 0xff, band: (target >>> 8) & 0xff };
}

export type MixerOpcode = (typeof MIXER_OPCODE)[keyof typeof MIXER_OPCODE];

/**
 * Which strip a deck feeds.
 *
 * A function rather than a literal at the call site, because §2's ruling is that adding M6's third
 * strip must be a call site and not a re-derivation — and the first thing a re-derivation gets wrong
 * is which input something was connected to.
 */
export function stripIndexForDeck(deckId: 'a' | 'b'): number {
  return deckId === 'b' ? 1 : 0;
}

/**
 * The inverse: which deck feeds a strip, or null for a strip no deck feeds (P4.8).
 *
 * ⚠️ **Here, beside its inverse, rather than as `DECK_IDS[strip]` at the call site** — which is what
 * P4.8's first draft did, and is precisely the re-derivation the comment above warns about. The two
 * happen to agree today because `DECK_IDS` is `['a', 'b']` and strips are numbered the same way;
 * they stop agreeing the moment M6 inserts the sampler's strip between them, and the symptom would
 * be an echo timed to the wrong deck's tempo — which sounds like an echo, just a wrong one.
 *
 * Null rather than a default, because M6's `SMP` strip genuinely has no deck behind it and a
 * beat-synced unit there has to run free rather than borrow deck A's grid.
 */
export function deckIdForStrip(strip: number): 'a' | 'b' | null {
  return strip === 0 ? 'a' : strip === 1 ? 'b' : null;
}

/** What the mixer processor is constructed with. Structured-cloned, so values only (P3.7). */
export interface MixerProcessorOptions {
  readonly commandRing: SharedArrayBuffer;
  readonly stripCount: number;
  /**
   * The one FR-26 signal ring, shared with both decks (P4.7).
   *
   * The mixer publishes the strips' pre-fader levels and the master's into the *same* frame the
   * decks write, which is what makes a channel meter and the master meter describe one block. It
   * runs after both decks in the same quantum, so by the time it arrives the frame is already open
   * and `openQuantum` hands it the slot rather than a new one.
   */
  readonly signals: SharedArrayBuffer;
  /**
   * Which ring `signals` is (P3.7's id, and P4.7 is its third user).
   *
   * ⚠️ **A number, because a `SharedArrayBuffer` cannot answer this.** `processorOptions` is
   * structured-cloned, and a cloned SAB shares memory while being a different JS object — so the
   * mixer's `signals` is not identity-equal to either deck's even though all three are one ring.
   * `sharedSignalPublisher` keys on this number, and its comment has the account of the bug that
   * shipped when the key was the buffer.
   */
  readonly signalsId: number;
}

/**
 * Which signal source a strip publishes into (P4.7).
 *
 * ⚠️ **Indexed by strip, and deliberately not derived by arithmetic.** `STRIP_A` is 3 and strip 0
 * is deck A's, so `SIGNAL_SOURCE.STRIP_A + strip` would happen to work today and would quietly
 * break the moment M6's sampler lands as a third strip with a source that is not adjacent. A table
 * is one line longer and says what it means.
 *
 * M6 appends `SMP`'s source here and to `SIGNAL_SOURCE` in the same edit — see ADR-L13 on the
 * third strip being a call site rather than a rewrite.
 */
export const STRIP_SIGNAL_SOURCE: readonly SignalSourceIndex[] = [
  SIGNAL_SOURCE.STRIP_A,
  SIGNAL_SOURCE.STRIP_B,
];

/**
 * An EQ knob's 0…1 position to the linear band gain it means (P4.10).
 *
 * Three properties this has to have, and they pull against each other:
 *
 *  1. **The bottom is a true kill.** Position 0 must be exactly 0, not −40 dB. FR-14 says "full kill
 *     at minimum" and `eq.ts` is built so 0 is reachable; a law that bottomed out at a small positive
 *     gain would quietly undo that.
 *  2. **Centre is unity**, so a knob a hand has not touched changes nothing.
 *  3. **It has to feel like an isolator on the way down.** Linear in amplitude is the obvious choice
 *     and is wrong: half travel would be −6 dB, which barely sounds like anything, and the whole
 *     bottom half of the knob would do the last 6 dB in a rush.
 *
 * Decibels solve (3) and cannot express (1) — there is no dB value for silence. So: **a power law
 * below centre**, `(position / centre) ^ 2.5`, which is smooth, reaches exactly zero at zero, and
 * spends its travel the way an ear expects — half travel is −15 dB, a fifth is −35 dB, and the last
 * fraction is the drop into silence.
 *
 * The exponent is a character choice like the crossovers, and 2.5 is chosen so that half travel lands
 * near −15 dB: deep enough that the knob is doing something audible by its midpoint, shallow enough
 * that the kill still feels like it belongs to the bottom of the travel rather than to the middle.
 *
 * Above centre it is linear in decibels up to `EQ_MAX_GAIN`, because a boost has no silence problem
 * and dB is what an ear reads.
 */
export function eqGainFromPosition(position: number, maxGain: number): number {
  if (!Number.isFinite(position)) {
    return TRIM_UNITY_GAIN;
  }
  const clamped = Math.min(1, Math.max(0, position));
  if (clamped >= TRIM_UNITY_POSITION) {
    const above = (clamped - TRIM_UNITY_POSITION) / TRIM_UNITY_POSITION;
    return gainFromDb(above * dbFromGain(maxGain));
  }
  return Math.pow(clamped / TRIM_UNITY_POSITION, EQ_KNOB_EXPONENT);
}

/** See `eqGainFromPosition`. A character choice, stated so it is not changed by accident. */
export const EQ_KNOB_EXPONENT = 2.5;

/**
 * The three EQ bands, in signal order (P4.3, moved here at P4.10).
 *
 * Here rather than in `audio/dsp/eq.ts` because it is **vocabulary rather than signal processing**:
 * the index crosses `postMessage` inside a `COMMAND_RECORD.TARGET`, the mixer panel names it, the
 * mapping table names it, and the worklet reads it back. The boundary rule `ui-no-engine-internals`
 * is what surfaced that — the UI needed `EQ_MAX_GAIN` and is not allowed to reach into `/audio/dsp`.
 *
 * Indices are stable. Never renumber one in place, for the reason `MIXER_OPCODE` gives.
 */
export const EQ_BAND = { LOW: 0, MID: 1, HIGH: 2 } as const;
export type EqBand = (typeof EQ_BAND)[keyof typeof EQ_BAND];
export const EQ_BAND_COUNT = 3;

/**
 * The most an EQ band may be boosted, linear.
 *
 * +6 dB. An isolator's job is removal, and a boost is the thing that makes one unusable: every band
 * up is a master that clips, and the headroom arithmetic above already sizes the sum for two decks
 * rather than for two decks with their bass boosted. Generous enough to lift a thin record, mean
 * enough that the limiter is not asked to fix a habit.
 */
export const EQ_MAX_GAIN = 2;

// ---------------------------------------------------------------------------
// FR-14's bipolar filter (P4.4)
// ---------------------------------------------------------------------------

/**
 * How wide the centre detent is, as a fraction of the knob's travel either side of centre.
 *
 * ⚠️ **The filter is genuinely bypassed inside this band — not set to a very open cutoff.** P4.4's
 * rule is explicit: *"Centre must be bypass, not 'a very open filter' — a filter that colours the
 * signal at rest is one a DJ cannot leave alone, and the difference is audible on a null test."*
 *
 * A low-pass parked at 16 kHz is not transparent. It is −3 dB at 16 kHz, it rotates phase across the
 * top octave, and it is in the path of every track all night. A DJ who can hear that becomes a DJ
 * who nudges the knob to "fix" it, which is the failure this rule exists to prevent.
 *
 * 3% of travel, which at the 1% keyboard step of `MixerPanel` is three presses either side — wide
 * enough that a hand finds it and a pointer settles into it, narrow enough that it does not eat the
 * useful part of the sweep. A real mixer solves this with a physical detent; this is the software
 * equivalent, and it is the reason the null test below can be **exact** rather than "below a floor".
 */
export const FILTER_DETENT = 0.03;

/**
 * The sweep's ends, in hertz.
 *
 * 30 Hz and 16 kHz, symmetric in the sense that matters: the low-pass runs from transparent down to
 * 30 Hz, and the high-pass from transparent up to 16 kHz, so a full turn either way removes almost
 * everything. Beyond 16 kHz there is nothing left to take away that most systems reproduce or most
 * listeners hear; below 30 Hz the same is true at the other end, and a cutoff that low mostly
 * removes the sub a club system is producing rather than the bass a listener notices.
 */
export const FILTER_MIN_HZ = 30;
export const FILTER_MAX_HZ = 16_000;

/** Which way a bipolar filter is leaning. `none` is the detent — the filter is out of the path. */
export type FilterMode = 'none' | 'lowpass' | 'highpass';

export interface FilterSetting {
  readonly mode: FilterMode;
  /** Hertz. Meaningless when `mode` is `none`, and set to `FILTER_MAX_HZ` so a reader is not
   * tempted to interpret a zero. */
  readonly cutoffHz: number;
}

/**
 * A bipolar filter knob's 0…1 position to what the filter should do.
 *
 * **Exponential in frequency, and that is the musical decision P4.4 asks to be written down.**
 * Linear in hertz is wrong and obviously so once stated: half the travel would sit above 8 kHz,
 * where a sweep does almost nothing a listener notices, and the entire audible move — the part a DJ
 * performs with — would be crammed into the last few percent. Pitch is logarithmic, so a knob that
 * sweeps it must be too. Equal movements of the hand then give equal movements in **octaves**, which
 * is what "sweep the filter" means to the person doing it.
 *
 * The range is nine octaves (30 Hz to 16 kHz), so at the 1% keyboard step each press is about a
 * tenth of an octave — fine enough to land where you meant.
 *
 * Left of centre is **low-pass** and right is **high-pass**, matching every DJ mixer: turning left
 * takes the top away, turning right takes the bottom away.
 */
export function filterFromPosition(position: number): FilterSetting {
  if (!Number.isFinite(position)) {
    return { mode: 'none', cutoffHz: FILTER_MAX_HZ };
  }
  const clamped = Math.min(1, Math.max(0, position));
  const offset = clamped - TRIM_UNITY_POSITION;

  // ⚠️ An epsilon, because `0.5 + 0.03` is not 0.53 and `0.53 - 0.5` is not 0.03. Without it the
  // stated edge of the detent lands a few ulps *outside* it, so a knob at exactly 0.53 filters
  // while the constant says it should not. A boundary that depends on ulps is a boundary nobody can
  // reason about, and a detent that is narrower than it claims is one a hand cannot trust.
  if (Math.abs(offset) <= FILTER_DETENT + 1e-9) {
    return { mode: 'none', cutoffHz: FILTER_MAX_HZ };
  }

  // How far into the usable travel this is, 0…1, measured from the edge of the detent so the sweep
  // starts where the detent ends rather than jumping.
  const span = TRIM_UNITY_POSITION - FILTER_DETENT;
  const travel = Math.min(1, (Math.abs(offset) - FILTER_DETENT) / span);
  const octaves = Math.log2(FILTER_MAX_HZ / FILTER_MIN_HZ);

  return offset < 0
    ? // Low-pass: transparent at the detent, closing down to FILTER_MIN_HZ at full left.
      { mode: 'lowpass', cutoffHz: FILTER_MAX_HZ * Math.pow(2, -travel * octaves) }
    : // High-pass: transparent at the detent, opening up to FILTER_MAX_HZ at full right.
      { mode: 'highpass', cutoffHz: FILTER_MIN_HZ * Math.pow(2, travel * octaves) };
}
